import React, { useState, useEffect } from "react";
import {
    Calendar,
    ArrowUpRight,
    ArrowDownRight,
    ShoppingBag,
    Disc,
    Mic2,
    Loader2
} from "lucide-react";
import { AdminDashboardService } from "../services/AdminDashboardService";
import { formatRelativeTime } from "../utils/dateUtils";

const StatCard = ({ title, value, change, trend, icon: Icon, loading }) => (
    <div className="bg-white dark:bg-[#1A1A1A] p-6 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm hover:shadow-md transition-shadow">
        {loading ? (
            <div className="animate-pulse">
                <div className="flex justify-between items-start mb-4">
                    <div className="w-12 h-12 bg-black/5 dark:bg-white/5 rounded-xl" />
                    <div className="w-12 h-4 bg-black/5 dark:bg-white/5 rounded" />
                </div>
                <div className="w-24 h-4 bg-black/5 dark:bg-white/5 rounded mb-2" />
                <div className="w-16 h-8 bg-black/5 dark:bg-white/5 rounded" />
            </div>
        ) : (
            <>
                <div className="flex justify-between items-start mb-4">
                    <div className="p-3 bg-jazz-brown/10 dark:bg-jazz-brown/20 rounded-xl text-jazz-brown">
                        <Icon size={24} />
                    </div>
                    <div className={`flex items-center gap-1 text-sm font-medium ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                        {trend === 'up' ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                        {change}
                    </div>
                </div>
                <div>
                    <h3 className="text-black dark:text-white/50 text-sm font-medium mb-1 uppercase tracking-wider">{title}</h3>
                    <p className="text-3xl font-bold text-black dark:text-white" style={{ fontFamily: "Playfair Display, serif" }}>{value}</p>
                </div>
            </>
        )}
    </div>
);

const ActivityItem = ({ title, time, type }) => (
    <div className="flex items-center gap-4 py-4 border-b border-black/5 dark:border-white/5 last:border-0">
        <div className={`w-2 h-2 rounded-full ${type === 'sale' ? 'bg-jazz-brown' :
            type === 'event' ? 'bg-black' : 'bg-gray-400'
            }`} />
        <div className="flex-1">
            <h4 className="text-sm font-medium text-black dark:text-white uppercase tracking-wider text-xs">{title}</h4>
            <p className="text-xs text-black dark:text-white/40">{formatRelativeTime(time)}</p>
        </div>
        <button className="text-xs font-bold text-jazz-brown hover:underline underline-offset-4">VIEW</button>
    </div>
);

const SkeletonItem = () => (
    <div className="flex items-center gap-4 py-4 border-b border-black/5 dark:border-white/5 animate-pulse">
        <div className="w-2 h-2 rounded-full bg-black/5 dark:bg-white/5" />
        <div className="flex-1 space-y-2">
            <div className="w-3/4 h-4 bg-black/5 dark:bg-white/5 rounded" />
            <div className="w-1/4 h-3 bg-black/5 dark:bg-white/5 rounded" />
        </div>
    </div>
);

const EmptyState = ({ message }) => (
    <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-16 h-16 bg-black/5 dark:bg-white/5 rounded-full flex items-center justify-center mb-4">
            <Loader2 className="text-black/20 dark:text-white/20 animate-spin" size={24} />
        </div>
        <p className="text-sm text-black italic">{message}</p>
    </div>
);

export default function Dashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);


    useEffect(() => {

        const fetchDashboardData = async () => {
            try {
                const result = await AdminDashboardService.getDashboardStats();
                setData(result);
            } catch (err) {
                console.error("Failed to load dashboard stats", err);
            } finally {
                setLoading(false);
            }
        };
        fetchDashboardData();
    }, []);

    const adminName = JSON.parse(localStorage.getItem("admin"))?.full_name || "Admin";
    const firstName = adminName.split(" ")[0];

    const stats = [
        { title: "Total Revenue", value: data?.stats?.total_revenue || "$0.00", change: "0%", trend: "up", icon: ShoppingBag },
        { title: "Music Content Sales", value: data?.stats?.music_content_sales || "0", change: "0%", trend: "up", icon: Disc },
        { title: "Upcoming Events", value: data?.stats?.upcoming_events || "0", change: "0", trend: "up", icon: Calendar },
        { title: "Press Mentions", value: data?.stats?.press_mentions || "0", change: "0%", trend: "down", icon: Mic2 },
    ];

    return (
        <div className="p-6 lg:p-10 max-w-7xl mx-auto">
            <div className="mb-10">
                <h1 className="text-3xl lg:text-4xl font-normal text-black dark:text-white mb-2" style={{ fontFamily: "Playfair Display, serif" }}>
                    Welcome back, {firstName}
                </h1>
                <p className="text-black dark:text-white/50 text-sm italic font-serif">
                    Here's what's happening with your artist profile today.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                {stats.map((stat, idx) => (
                    <StatCard key={idx} {...stat} loading={loading} />
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                <div className="lg:col-span-2 bg-white dark:bg-[#1A1A1A] p-8 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-bold text-black dark:text-white" style={{ fontFamily: "Playfair Display, serif" }}>
                            Recent Activity
                        </h2>
                        <button className="text-xs font-bold tracking-widest text-jazz-brown hover:text-black dark:hover:text-white transition-colors">
                            SEE ALL ACTIVITY
                        </button>
                    </div>
                    <div className="space-y-1">
                        {loading ? (
                            Array(5).fill(0).map((_, i) => <SkeletonItem key={i} />)
                        ) : data?.recent_activity?.length > 0 ? (
                            data.recent_activity.map((item, idx) => (
                                <ActivityItem key={idx} {...item} />
                            ))
                        ) : (
                            <EmptyState message="No recent activity to display yet." />
                        )}
                    </div>
                </div>

                <div className="space-y-6">
                    <div className="bg-black text-white p-8 rounded-2xl shadow-xl relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-jazz-brown/20 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
                        <h3 className="text-lg font-bold mb-4 relative z-10" style={{ fontFamily: "Playfair Display, serif" }}>
                            Quick Action
                        </h3>
                        <p className="text-white/80 text-sm mb-6 relative z-10">
                            Instantly add new recordings or sheet music to your catalog.
                        </p>
                        <button className="w-full bg-white text-black py-3 rounded-lg text-xs font-bold tracking-widest hover:bg-jazz-brown hover:text-white transition-all relative z-10">
                            UPLOAD NEW CONTENT
                        </button>
                    </div>

                    <div className="bg-white dark:bg-[#1A1A1A] p-8 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm">
                        <h3 className="text-lg font-bold mb-4 text-black dark:text-white" style={{ fontFamily: "Playfair Display, serif" }}>
                            System Status
                        </h3>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-black dark:text-white/50">Website</span>
                                <span className="flex items-center gap-2 text-xs font-bold text-green-600">
                                    <div className="w-1.5 h-1.5 bg-green-600 rounded-full animate-pulse" />
                                    ONLINE
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-black dark:text-white/50">Shop API</span>
                                <span className="flex items-center gap-2 text-xs font-bold text-green-600">
                                    <div className="w-1.5 h-1.5 bg-green-600 rounded-full" />
                                    OPERATIONAL
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-white dark:bg-[#1A1A1A] p-8 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm">
                    <h2 className="text-xl font-bold text-black dark:text-white mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
                        Shop & Music Inventory
                    </h2>
                    {loading ? (
                        <div className="space-y-6">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="animate-pulse">
                                    <div className="flex justify-between mb-2">
                                        <div className="w-24 h-3 bg-black/5 dark:bg-white/5 rounded" />
                                        <div className="w-16 h-3 bg-black/5 dark:bg-white/5 rounded" />
                                    </div>
                                    <div className="w-full h-1.5 bg-black/5 dark:bg-white/5 rounded-full" />
                                </div>
                            ))}
                        </div>
                    ) : data?.inventory?.digital_tracks > 0 || data?.inventory?.sheet_music > 0 || data?.inventory?.merch > 0 ? (
                        <div className="space-y-6">
                            <div className="p-4 bg-[#FAFAFA] dark:bg-[#222] rounded-xl border border-black/5">
                                <div className="flex justify-between items-center mb-2">
                                    <h4 className="text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest">Digital Downloads</h4>
                                    <span className="text-sm font-bold text-jazz-brown">{data.inventory.digital_tracks} Tracks</span>
                                </div>
                                <div className="w-full bg-gray-200 h-1.5 rounded-full overflow-hidden">
                                    <div className="bg-jazz-brown h-full" style={{ width: `${Math.min(data.inventory.digital_tracks * 5, 100)}%` }} />
                                </div>
                            </div>
                            <div className="p-4 bg-[#FAFAFA] dark:bg-[#222] rounded-xl border border-black/5">
                                <div className="flex justify-between items-center mb-2">
                                    <h4 className="text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest">Sheet Music</h4>
                                    <span className="text-sm font-bold text-black dark:text-white">{data.inventory.sheet_music} Items</span>
                                </div>
                                <div className="w-full bg-gray-200 h-1.5 rounded-full overflow-hidden">
                                    <div className="bg-black h-full" style={{ width: `${Math.min(data.inventory.sheet_music * 10, 100)}%` }} />
                                </div>
                            </div>
                            <div className="p-4 bg-[#FAFAFA] dark:bg-[#222] rounded-xl border border-black/5">
                                <div className="flex justify-between items-center mb-2">
                                    <h4 className="text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest">Artist Merch</h4>
                                    <span className="text-sm font-bold text-jazz-brown">{data.inventory.merch} Products</span>
                                </div>
                                <div className="w-full bg-gray-200 h-1.5 rounded-full overflow-hidden">
                                    <div className="bg-jazz-brown h-full" style={{ width: `${Math.min(data.inventory.merch * 10, 100)}%` }} />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <EmptyState message="Inventory is currently empty. Start by adding items in the catalog." />
                    )}

                    <div className="mt-8 space-y-6">
                        {data?.recent_music_items?.length > 0 && (
                            <div className="max-w-md">
                                <h3 className="text-sm font-bold text-black dark:text-white mb-4 uppercase tracking-widest border-b border-black/5 pb-2">Recently Uploaded Music</h3>
                                <div className="space-y-3">
                                    {data.recent_music_items.map((item) => (
                                        <div key={item.id} className="flex justify-between items-center p-3 bg-black/5 dark:bg-white/5 rounded-lg group hover:bg-jazz-brown/5 transition-colors">
                                            <span className="text-xs font-medium text-black dark:text-white truncate max-w-[200px]">{item.title}</span>
                                            <span className="text-[10px] uppercase font-bold text-white bg-black px-2 py-0.5 rounded shadow-sm">{item.type}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-white dark:bg-[#1A1A1A] p-8 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm">
                    <h2 className="text-xl font-bold text-black dark:text-white mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
                        Press Highlights
                    </h2>
                    <div className="space-y-4">
                        {loading ? (
                            [1, 2, 3].map(i => (
                                <div key={i} className="p-4 border-l-2 border-black/5 bg-black/5 animate-pulse rounded-r-xl">
                                    <div className="w-full h-4 bg-black/5 dark:bg-white/5 rounded mb-2" />
                                    <div className="flex justify-between">
                                        <div className="w-24 h-3 bg-black/5 dark:bg-white/5 rounded" />
                                        <div className="w-12 h-3 bg-black/5 dark:bg-white/5 rounded" />
                                    </div>
                                </div>
                            ))
                        ) : data?.press_highlights?.length > 0 ? (
                            data.press_highlights.map((item, idx) => (
                                <div key={idx} className="p-4 border-l-2 border-jazz-brown bg-jazz-brown/5 rounded-r-xl group hover:bg-jazz-brown/10 transition-colors">
                                    <p className="text-sm font-serif italic mb-2 text-black dark:text-white">"{item.quote}"</p>
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs font-bold uppercase tracking-widest text-black dark:text-white">{item.publication}</span>
                                        <span className="text-xs text-black dark:text-white/50">{item.date}</span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <EmptyState message="No press highlights to feature." />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
